<template>
  <v-container id="delivery-table" fluid>
    <v-card class="flex-column" outlined>
      <v-data-table
        :headers="headers"
        :items="deliveryMethods"
        :options.sync="options"
        :server-items-length="totalDeliveryMethods"
        :footer-props="footerProps"
        :loading="loadingData"
        :loading-text="$t('deliveryMethod.loadingMethodText')"
        :no-data-text="$t('deliveryMethod.noMethod')"
        mobile-breakpoint="481"
        item-key="id"
        :header-props="headerProps"
        dense
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-btn color="primary" class="mr-1" outlined :to="'/delivery-methods/add'">
              <v-icon>mdi-package-variant-closed-plus</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn class="mb-2" color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.carrier="{ item }">
          {{ `${item.carrier ? item.carrier : $t('deliveryMethod.none')}` }}
        </template>
        <template v-slot:item.delivery_constraints.by_default.delivery_delay="{ item }">
          {{ `${item.delivery_constraints.by_default.delivery_delay} ${$t('deliveryMethod.hours')}` }}
        </template>
        <template v-slot:item.delivery_constraints.by_default.price="{ item }">
          {{ `${item.delivery_constraints.by_default.price} ${item.currency.code}` }}
        </template>
        <template v-slot:item.totalWeight="{ item }">
          <span v-if="item.eligibility_conditions.total_weight.max === undefined">
            <v-icon x-small>mdi-greater-than-or-equal</v-icon>
            {{ item.eligibility_conditions.total_weight.min }}
          </span>
          <span v-else>
            {{ item.eligibility_conditions.total_price.min }} - {{ item.eligibility_conditions.total_weight.max }}
          </span>
          {{ $t('deliveryMethod.kilogramShort') }}
        </template>
        <template v-slot:item.totalPrice="{ item }">
          <span v-if="item.eligibility_conditions.total_price.max === undefined">
            <v-icon x-small>mdi-greater-than-or-equal</v-icon>
            {{ item.eligibility_conditions.total_price.min }}
          </span>
          <span v-else>
            {{ item.eligibility_conditions.total_price.min }} - {{ item.eligibility_conditions.total_price.max }}
          </span>
          {{ item.currency.code }}
        </template>
        <template v-slot:item.activate="{ item }">
          <v-simple-checkbox v-model="item.activate" disabled></v-simple-checkbox>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn
            :class="[$vuetify.breakpoint.width >= 481 ? '' : 'pr-0']"
            text
            color="primary"
            @click="handleClick(item._id)"
          >
            <v-icon v-if="$vuetify.breakpoint.width >= 650 && $vuetify.breakpoint.width <= 768">mdi-magnify</v-icon>
            <span v-else>{{ $t('order.viewDetails') }}</span>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
  import { EventBus } from '@/event-bus'
  import { mapState } from 'vuex'

  export default {
    name: 'deliveryTable',
    data() {
      return {
        loadingData: false,
        options: {
          itemsPerPage: 15,
          sortDesc: [],
        },
        deliveryMethods: undefined,
        totalDeliveryMethods: 0,
      }
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset current page number and delivery methods per page if the previous page wasn't the details of a delivery method
      if (previousPath !== 'delivery-methods') {
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
      }
      this.deliveryMethods = []
      this.list()
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService']),
      headers() {
        return [
          {
            text: this.$t('deliveryMethod.name'),
            value: 'name',
          },
          // {
          //   text: this.$t('deliveryMethod.carrier'),
          //   value: 'carrier'
          // },
          {
            text: this.$t('deliveryMethod.defaultDelay'),
            value: 'delivery_constraints.by_default.delivery_delay',
          },
          {
            text: this.$t('deliveryMethod.defaultPrice'),
            value: 'delivery_constraints.by_default.price',
          },
          {
            text: this.$t('deliveryMethod.weightLimit'),
            value: 'totalWeight',
            sortable: false,
          },
          {
            text: this.$t('deliveryMethod.priceLimit'),
            value: 'totalPrice',
            sortable: false,
          },
          {
            text: this.$t('deliveryMethod.active'),
            value: 'activate',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ]
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('deliveryMethod.methodsPerPages'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('order.sortByText'),
        }
      },
    },
    methods: {
      handleClick(id) {
        this.$router.push('/delivery-methods/' + id)
      },
      list() {
        this.loadingData = true
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: page,
            itemsPerPage: itemsPerPage,
            locationId: this.isCustomerService ? undefined : this.currentStore,
            sortBy: sortBy.length == 0 ? 'name' : sortBy,
            sortDesc: sortDesc.length == 0 ? true : sortDesc,
          },
        }

        this.$Amplify.API.get('carriers', '/v1/int/delivery-methods', myInit)
          .then((res) => {
            this.deliveryMethods = res.data
            this.totalDeliveryMethods = res.nbDeliveryMethods
            this.loadingData = false
            if (this.totalDeliveryMethods < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalDeliveryMethods / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
          })
          .catch(() => {
            this.loadingData = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('deliveryMethod.methodsPerPages'),
              color: 'error',
            })
          })
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.list()
      },
    },
  }
</script>
